import { StyleSheet } from 'aphrodite';
import {
	aceGray,
	black,
	borderColor,
	brandPrimary,
	brandSecondary,
	lightGray,
	midGrey,
	navigation,
	warningDark,
	white,
} from '../../../../web/styles/colors';
import { baseStyles, BreakPoints, CssSelectors, Layouts } from '../../../../web/styles/styles';
import { THEME } from '../../../components-v2/Theme/theme';

const mobileBreakpoint = 720;

const thinkingBackgroundColorAnimation = {
	'0%': {
		backgroundColor: '#C7D4DB',
		transform: 'translate3d(0, 0%, 0)',
	},
	'100%': {},
	'15%': {
		backgroundColor: '#F9F9F9',
		transform: 'translate3d(0, -30%, 0)',
	},
	'35%': {
		backgroundColor: '#C7D4DB',
		transform: 'translate3d(0, 0%, 0)',
	},
	'80%': {},
};

const thinkingElementAnimation = (elementIndex: number) => {
	const baseDuration = 225;
	const animationDuration = 6 * baseDuration;
	const animationDelay = (elementIndex - 1) * (2 * baseDuration);
	return {
		animationDelay: `${animationDelay}ms`,
		animationDuration: `${animationDuration}ms`,
		animationIterationCount: 'infinite',
		animationName: [thinkingBackgroundColorAnimation],
		animationTimingFunction: 'ease-in',
	};
};

export const styleSheet = StyleSheet.create({
	textArea: {
		...baseStyles.textField,
		'-webkit-appearance': 'none',
		'::placeholder': {
			color: navigation,
		},
		':focus': {
			outline: 'none',
		},
		flexGrow: 1,
		resize: 'none',
	},
	author: {
		display: 'flex',
		flexDirection: 'column',
		marginLeft: 8,
	},
	avatar: {
		height: 48,
		width: 48,
	},
	successScreen: {
		height: '100%',
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		flexDirection: 'column',
	},
	navIconSection: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
		width: '8%',
		backgroundColor: lightGray,
		cursor: 'pointer',
	},
	borderRight: {
		borderRight: `1px solid ${borderColor}`,
	},
	borderLeft: {
		borderLeft: `1px solid ${borderColor}`,
	},
	body: {
		'@media only screen and (min-width: 800px)': {
			width: 800,
		},
		display: 'flex',
		flexDirection: 'column',
	},
	buttonContainer: {
		display: 'flex',
		marginTop: 36,
		...CssSelectors.allChildren(
			{
				marginLeft: '0.5rem',
			},
			':not(:first-child)'
		),
	},
	closeButton: {
		height: 18,
	},
	errorContainer: {
		color: warningDark,
	},
	header: {
		alignItems: 'center',
		background: 'linear-gradient(158deg, #009FFF, #0066FF)',
		borderTopLeftRadius: 4,
		borderTopRightRadius: 4,
		color: white,
		display: 'flex',
		justifyContent: 'space-between',
		padding: `8px 16px`,
	},
	locationIcon: {
		left: 0,
		position: 'absolute',
		top: '50%',
		transform: 'translate(0, -50%)',
	},
	location: {
		color: brandPrimary,
		fontSize: 10,
		marginTop: 8,
		paddingLeft: 24,
		position: 'relative',
		[THEME.queries.md]: {
			display: 'inline-block',
			width: 'auto',
		},
		[BreakPoints.forMaxWidth(mobileBreakpoint)]: {
			marginTop: 5,
		},
	},
	modal: {
		...CssSelectors.allDescendants(
			{
				borderRadius: 6,
				boxSizing: 'content-box',
				padding: 0,
			},
			'.modal-overlay-content'
		),
	},
	aiResponseContainer: {
		flex: '1',
	},
	responseContainer: {
		flex: '1',
		display: `flex`,
		flexDirection: `column`,
		padding: `16px 16px 24px`,
	},
	responseRow: {
		borderTop: `1px solid ${borderColor}`,
		display: 'flex',
		flexDirection: 'row',
		height: 424,
	},
	responseTitle: {
		marginTop: 0,
	},
	pill: {
		alignItems: 'center',
		background: brandSecondary,
		borderRadius: 60,
		boxSizing: 'border-box',
		color: white,
		display: 'flex',
		fontSize: 12,
		height: 'auto',
		margin: 0,
		marginTop: 8,
		padding: '14px 32px',
		position: 'relative',
	},
	responsesCount: {
		display: 'flex',
		justifyContent: 'flex-end',
		textAlign: 'start',
		color: aceGray,
		fontSize: 12,
		paddingTop: 2,
	},
	rating: {
		...CssSelectors.allChildren({
			display: 'block',
		}),
	},
	ratingStar: {
		height: 16,
		width: 16,
	},
	emptyComment: {
		color: midGrey,
	},
	reviewerColumn: {
		borderRight: `1px solid ${borderColor}`,
		display: 'flex',
		flexDirection: 'column',
		padding: '0 16px 16px 16px',
		width: '30%',
	},
	reviewer: {
		display: 'flex',
		flexDirection: 'row',
	},
	reviewColumn: {
		scrollbarGutter: 'stable',
		overflowY: 'auto',
		padding: 16,
		width: '54%',
		maxHeight: 72,
	},
	reviewerName: {
		color: black,
		fontSize: 12,
	},
	reviewRow: {
		display: 'flex',
		flexDirection: 'row',
		width: '100%',
	},
	reviewText: {
		fontSize: 12,
		marginTop: 8,
		marginBottom: 8,
		textAlign: 'left',
		maxWidth: 375,
	},
	thinking: {
		alignItems: 'center',
		boxSizing: 'border-box',
		display: 'flex',
		position: 'relative',
		...CssSelectors.allChildren({
			':not(:first-child)': {
				marginLeft: 5,
			},
			':nth-child(1)': thinkingElementAnimation(1),
			':nth-child(2)': thinkingElementAnimation(2),
			':nth-child(3)': thinkingElementAnimation(3),
			backgroundColor: '#C7D4DB',
			borderRadius: '50%',
			height: 12,
			width: 12,
		}),
	},
	thinkingLionImage: {
		paddingTop: 56,
	},
	thinkingModalMessages: {
		...Layouts.verticalStack(15),
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
	},
	timeAgo: {
		color: black,
		fontSize: 12,
		fontWeight: 600,
		opacity: 0.5,
	},
});
